import type { IControlSelectCampaignItem } from '../components';
import type { SelectValueType } from '../../type';

import { useCGTemplateListing } from '~/modules/campaign-group-template';
import { EReportControlId } from '~/presentation/reports/common/type';
import { useControlDataChange } from '~/presentation/reports/common';
import { OPERATOR_IS_ONE_OF } from '~/modules/report-analytic';
import { isCampaignItemOperator } from '../utils';
import { SelectVariantType } from '../../type';
import { sortByConditions } from '~/utils';
import { useEffect, useMemo } from 'react';

type IUseSelectCampaignItem = Pick<IControlSelectCampaignItem, 'configs' | 'dependentControls'>;
export const useSelectCampaignItem = (props: IUseSelectCampaignItem) => {
  const { dependentControls, configs } = props;

  const campaignGroupTemplateListing = useCGTemplateListing();
  const { emitControlChange } = useControlDataChange();

  const campaignGroupId = useMemo(() => {
    return dependentControls?.find((control) => control.id === EReportControlId.GROUP_CAMPAIGN)
      ?.value as string;
  }, [dependentControls]);

  const campaignGroup = useMemo(() => {
    return campaignGroupTemplateListing.data.find((campaign) => campaign.id === campaignGroupId);
  }, [campaignGroupTemplateListing.data, campaignGroupId]);

  const campaignGroupCampaigns = useMemo(() => {
    if (!campaignGroup) return [];

    const dataCampaigns = campaignGroup.campaigns || [];
    const dataCampaignTemplates = dataCampaigns.filter((item) => !item.deletedAt);
    return sortByConditions(dataCampaignTemplates, [
      {
        attr: 'createdAt',
        order: 'desc',
      },
    ]);
  }, [campaignGroup]);

  const campaignGroupCampaignsIds = useMemo(() => {
    return campaignGroupCampaigns.map((campaign) => campaign.id);
  }, [campaignGroupCampaigns]);

  const updateValues = () => {
    const values: Record<string, SelectValueType | undefined> = {};
    configs?.forEach((config) => {
      const isOperator = isCampaignItemOperator(config.id);

      if (isOperator) {
        values[config.id] = OPERATOR_IS_ONE_OF;
      } else if (config.variant === SelectVariantType.ACTION_LIST) {
        values[config.id] = undefined;
      } else {
        const defaultValues = Array.isArray(config.defaultValue)
          ? config.defaultValue
          : [config.defaultValue];
        const isDefault =
          defaultValues.length > 0 &&
          defaultValues.every((val) => campaignGroupCampaignsIds.includes(val as string));

        values[config.id] = isDefault ? config.defaultValue : campaignGroupCampaignsIds;
      }
    });
    emitControlChange(values);
  };

  useEffect(() => {
    if (!campaignGroupId) return;
    updateValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignGroupId]);

  return {
    campaignGroupCampaigns,
    campaignGroupId,
  };
};
